import { useCallback, useState } from 'react';

import { useAuth } from '@contexts/User.context';

/**
 * Per-user, per-browser record of which tours have been dismissed or completed.
 *
 * Keyed by user uuid rather than stored flat: two people sharing a machine
 * would otherwise inherit each other's onboarding, and the second one would
 * never be shown anything. This is deliberately not server state — the payoff
 * for cross-device memory is "sees the tour one extra time", which is not worth
 * blocking on a backend field for.
 *
 * Every access is wrapped: `localStorage` throws outright in some privacy
 * modes, and a tour must never be the reason a project view fails to render.
 */

const STORAGE_PREFIX = 'biodrone.tour.seen';

const storageKey = (tourId, userUuid) => `${STORAGE_PREFIX}.${tourId}.${userUuid ?? 'anonymous'}`;

const readSeen = key => {
	try {
		return window.localStorage.getItem(key) === '1';
	} catch {
		// Unreadable storage means we can't prove it was seen. Showing a tour
		// twice is a smaller failure than never showing it at all.
		return false;
	}
};

/**
 * @param {string} tourId
 * @returns {{ seen: boolean, markSeen: () => void }}
 */
const useTourSeen = tourId => {
	const { currentUser } = useAuth();
	const key = storageKey(tourId, currentUser?.uuid);

	// Read once on mount. The value only changes through this hook, so there is
	// nothing to subscribe to.
	const [seen, setSeen] = useState(() => readSeen(key));

	const markSeen = useCallback(() => {
		setSeen(true);
		try {
			window.localStorage.setItem(key, '1');
		} catch {
			// Written state is a convenience; the in-memory flag still stops the
			// tour re-opening for the rest of this session.
		}
	}, [key]);

	return { seen, markSeen };
};

export default useTourSeen;
